import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import SectionLoader from '../Loader/SectionLoader';
import BasicModalBody from '../BasicModal/BasicModalBody';
import Paragraph from '../Paragraph/Paragraph';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import useProductPrice from '../../util/hooks/useProductPrice';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import ModalButtons from '../Modal/ModalButtons';
import { uFlexColumn } from '../../assets/styles/utility';
import RowItemElement from '../Buy/RowItem';
import {
  getCurrentAccount,
  getCurrentAccountUid,
  selectAccountAgreementHtml,
} from '../../store/selectors/accountSelectors';
import {
  fetchAccountAddresses,
  fetchAccountAgreement,
  submitAccountAgreements,
} from '../../store/actions/account/accountActions';
import { selectIsLoadingByActionTypes } from '../../store/selectors/loadingSelectors';
import {
  ACCOUNT_SETTINGS_LOADING,
  FETCH_ACCOUNT_ADDRESSES_LOADING,
  FETCH_ACCOUNT_AGREEMENT_HTML_LOADING,
} from '../../store/actions/account/accountActionConstants';
import { PRICE_ALERT_LOADING } from '../../store/actions/priceAlerts/priceAlertsActionConstants';
import { fetchPortfolioBalances } from '../../store/actions/portfolio/portfolioActions';
import useGetCountries from '../../util/hooks/useGetCountries';
import { useBrokerCode } from '../../util/hooks/useBrokerCode';
import ErrorDrawer from '../Buy/ErrorDrawer';
import useErrorDrawer from '../../util/hooks/useErrorDrawer';
import {
  selectSettings,
  selectPortalGatewayISOCodesListToIgnoreAreaCode,
} from '../../store/selectors/settingsSelectors';
import { ReactComponent as InfoIcon } from '../../assets/images/priceAlerts/info-blue.svg';
import { ReactComponent as Envelope } from '../../assets/images/priceAlerts/envelope.svg';
import { ReactComponent as MobileDevice } from '../../assets/images/priceAlerts/mobileDevice.svg';
import {
  selectAuthUser,
  selectUserPhoneNumbers,
} from '../../store/selectors/userSelectors';
import {
  patchPriceAlert,
  postPriceAlert,
  fetchPriceAlerts,
} from '../../store/actions/priceAlerts/priceAlertsActions';
import { selectPriceAlerts } from '../../store/selectors/priceAlertsSelectors';
import useGtmHook from '../../util/hooks/useGtmHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { PRICE_ALERT_DEEPLINK } from '../../constants/sessionStorage';
import { GS } from '../../constants/Brokers';
import {
  trackBuyStarted,
  trackPriceAlertStarted,
  trackPriceAlertCreated,
  trackPriceAlertEdited,
  trackPriceAlertReactivated,
  ENTRY_POINTS,
} from './Common/gtmEventHelper';
import { AGREEMENT_TYPES } from '../../constants/agreementConstants';
import { fetchUserPhoneNumbers } from '../../store/actions/user/userActions';
import {
  formatPhoneNumber,
  formatPhoneNumberNationalAsString,
} from '../../util/helpers/phoneNumberHelpers';
import ChangeMobileNumber from './ChangeMobileNumber';
import AlertInfoDrawer from './AlertInfoDrawer';
import CheckboxGTT from '../InputFields/CheckboxGTT';
import PrintModal from '../PrintModal/PrintModal';
import AgreementDisplay from '../Agreements/AgreementDisplay';
import {
  ProductName,
  ProductPrice,
  ProductHeadingRow,
  ProductHeadingPrice,
  InfoBanner as BaseInfoBanner,
  FullWidthButtonWrapper,
  Divider,
} from './Common';

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: stretch;
`;

const PriceInputWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const DollarSign = styled.span`
  position: absolute;
  left: ${pxToRem(16)};
  font-size: 16px;
  font-weight: 500;
  color: ${themeColors.colorTextSecondary};
  pointer-events: none;
  line-height: 140%;
`;

const PriceInput = styled.input`
  width: 100%;
  height: ${pxToRem(50)};
  padding: ${pxToRem(12)} ${pxToRem(16)} ${pxToRem(12)} ${pxToRem(32)};
  border: 1px solid
    ${({ isError }) =>
      isError ? themeColors.colorError : themeColors.colorCardBorder};
  border-radius: ${pxToRem(8)};
  font-size: 16px;
  font-weight: 500;
  color: ${themeColors.colorTextSecondary};
  background-color: ${themeColors.colorInputBg};

  &:focus {
    outline: none;
    border-color: ${({ isError }) =>
      isError ? themeColors.colorError : 'transparent'};
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    height: 38px;
    border-radius: 4px;
  }
`;

const InputError = styled.div`
  ${uFlexColumn};
  color: ${themeColors.colorError};
  font-size: 14px;
  line-height: 1.35;
  font-weight: 500;
  margin: ${pxToRem(4)} 0;
`;

const NotifyLabel = styled(Paragraph)`
  font-size: 14px;
  font-weight: 500;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
  margin-bottom: ${pxToRem(8)};
  margin-top: 24px;
`;

const NotifyViaLabel = styled(Paragraph)`
  font-size: 14px;
  font-weight: 500;
  leading-trim: NONE;
  line-height: 140%;
  letter-spacing: 0%;
  vertical-align: middle;
  color: ${themeColors.colorTextSecondary};
  margin-top: ${pxToRem(16)};
`;

const SmsLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
  font-size: 14px;
  font-weight: 500;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    gap: 10px;
  }
`;

const ConsentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(8)};
  margin-top: ${pxToRem(8)};
`;

const SmsConsentText = styled(Paragraph)`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
  text-align: left;
  overflow-wrap: break-word;
`;

const TermsLink = styled.span`
  color: ${themeColors.colorLinkPrimary};
  cursor: pointer;
  text-decoration: underline;
`;

const RowItemClickLabel = styled.span`
  font-family: ${variables.family.goldSilver};
  color: ${themeColors.colorTextSecondary};
  font-size: 16px;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: 0%;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const AlertRowLabel = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  font-weight: 500;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
`;

// The svg has an 18x18 viewBox with the glyph padded inside it, so the svg is
// scaled up (svg = 18 * target / glyph) to render the glyph at the design size:
// envelope glyph 14.625x11.25 -> 17.88x13.75 (desktop) / 14.62x11.25 (mobile).
const StyledEnvelope = styled(Envelope)`
  width: 22px !important;
  height: 22px !important;
  color: ${themeColors.colorPrimary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: 18px !important;
    height: 18px !important;
  }
`;

const StyledMobileDevice = styled(MobileDevice)`
  width: 22px !important;
  height: 22px !important;
  color: ${themeColors.colorPrimary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: 18px !important;
    height: 18px !important;
  }
`;

const AlertRowValue = styled.span`
  display: flex;
  align-items: center;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};

  svg {
    flex-shrink: 0;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    gap: 4px;
  }
`;

const AlertRowText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${themeColors.colorTextSecondary};
`;

const InfoBanner = styled(BaseInfoBanner)`
  width: 100%;
`;

const AlertEntry = ({
  wizardData,
  isProductPage,
  setWizardData,
  handleNext,
  handleBack,
  setWizardTitle,
  isAddAlertFlow,
  setGoBackHandler,
  setCloseHandler,
  // stepIndex,
  // currentStepIndex,
  // isUtmaAccount,
  // isIRAAccount,
  // isDeliveryOnlyAccount,
  // isPendingAccount,
  // isInstaVaultProduct,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ecommerceGtmEvent } = useGtmHook();
  const formikRef = useRef(null);
  const { storageProduct } = wizardData;
  const locations = wizardData?.storageProduct.Locations || [];
  const accountUid = useSelector(getCurrentAccountUid);
  const countries = useGetCountries();
  const settings = useSelector(selectSettings);
  const brokerCode = useBrokerCode();
  const account = useSelector(getCurrentAccount);
  const userInfo = useSelector(selectAuthUser);
  const userPhoneNumbers = useSelector(selectUserPhoneNumbers);
  const initialPhone = userPhoneNumbers?.[0] ?? null;

  const selectedISOCodesListToIgnoreAreaCode = useSelector(
    selectPortalGatewayISOCodesListToIgnoreAreaCode,
  );
  const isTestAccount = account?.IsTestAccount;

  const {
    isOpen: isErrorOpen,
    errorTitle,
    errorMessage,
    showError,
    hideError,
  } = useErrorDrawer();

  const [isAlertInfoOpen, setIsAlertInfoOpen] = useState(isAddAlertFlow);
  const [isChangeMobileNumberOpen, setIsChangeMobileNumberOpen] = useState(
    false,
  );
  // The specific alert being edited, read FRESH from the already-fresh price
  // alerts LIST slice (state.priceAlerts.data). The list is refetched on
  // modal open and after every save from the collection endpoint (confirmed
  // to reflect the just-PATCHed country immediately), and each entry carries
  // the full structured { Iso3CountryCode, AreaCode, LocalNumber } Phone plus
  // NotifyViaSMS. Deriving from it every render (rather than latching a
  // one-shot copy of the separate, never-PATCH-updated singular slice) is
  // what makes the flag self-correct instead of sticking stale.
  const priceAlerts = useSelector(selectPriceAlerts);
  const currentAlert = useMemo(
    () =>
      wizardData?.priceAlertUid
        ? (priceAlerts || []).find(
            (a) => a.PriceAlertUid === wizardData.priceAlertUid,
          )
        : null,
    [priceAlerts, wizardData?.priceAlertUid],
  );
  // User's in-session SMS toggle; null = untouched → fall back to the alert's
  // own value. User's in-session Changed number (from the drawer); seeds the
  // Set-Again flow via wizardData.phone but stays null for Edit (onEditAlert
  // passes phone: undefined), so Edit derives from the fresh currentAlert.
  const [smsOverride, setSmsOverride] = useState(null);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isAgreementDownloading, setIsAgreementDownloading] = useState(false);
  const [isSubmittingAgreement, setIsSubmittingAgreement] = useState(false);
  // The agreement HTML is fetched inside AgreementDisplay (rendered as the
  // terms modal's children). Treat the modal as "content loading" while that
  // fetch is in flight OR the HTML hasn't arrived yet, so the Accept button
  // and the download link don't flash in before the agreement is actually
  // ready. Kept separate from isAgreementDownloading (the PDF pull) so the two
  // can render distinct loaders: a full overlay for the initial content load
  // vs. an inline spinner on the download link for the PDF fetch.
  const agreementHtml = useSelector(selectAccountAgreementHtml);
  const isAgreementHtmlLoading = useSelector(
    selectIsLoadingByActionTypes([FETCH_ACCOUNT_AGREEMENT_HTML_LOADING]),
  );
  const isAgreementContentLoading = isAgreementHtmlLoading || !agreementHtml;
  const [pendingPhone, setPendingPhone] = useState(
    () => wizardData?.phone ?? null,
  );
  const [isPriceFocused, setIsPriceFocused] = useState(false);
  const wasAlertSuccessRef = useRef(false);

  const derivedSms = wizardData?.isEditAlert
    ? !!currentAlert?.NotifyViaSMS // Edit: authoritative fresh list value
    : !!wizardData?.isSmsChecked; // Add / Set-Again: the wizard seed
  const isSmsChecked = smsOverride ?? derivedSms;
  // The alert's own saved phone (Edit, SMS on), fresh from the list.
  const alertSavedPhone =
    wizardData?.isEditAlert && currentAlert?.NotifyViaSMS
      ? currentAlert.Phone
      : null;
  // Priority: user's in-session number > alert's fresh saved phone > account
  // default (userPhoneNumbers).
  const activePhone = pendingPhone || alertSavedPhone;
  const structuredPhone =
    isSmsChecked && activePhone ? activePhone : initialPhone;

  // Email shown on the Add/Edit screen:
  //  - Add / Add Another: the logged-in user's email.
  //  - Edit: the email of the user who SUBMITTED the alert (never the current
  //    editor's). It is also never changed on save — the PATCH omits Email.
  // Falls back to the logged-in email while the alert record has no Email yet.
  const displayEmail = wizardData?.isEditAlert
    ? currentAlert?.Email ?? userInfo?.Email
    : userInfo?.Email;
  const formattedPhoneNumber = formatPhoneNumber(
    structuredPhone,
    countries,
    selectedISOCodesListToIgnoreAreaCode,
  );
  const isBaseLoading = useSelector(
    selectIsLoadingByActionTypes([
      FETCH_ACCOUNT_ADDRESSES_LOADING,
      ACCOUNT_SETTINGS_LOADING,
      PRICE_ALERT_LOADING,
    ]),
  );
  // Block the whole section (and thus the "Change" click) on entering Edit
  // Alert until this alert's fresh list entry is available, so "Change" can
  // never open against data we don't have yet. Once currentAlert is present,
  // structuredPhone derives from it directly, so it stays correct (and
  // self-corrects if the list later refreshes) with no stale latch.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditDataReady =
    !(wizardData?.isEditAlert && wizardData?.priceAlertUid) || !!currentAlert;
  const isLoading =
    isBaseLoading || isSubmitting || isSubmittingAgreement || !isEditDataReady;

  const activeTab = 'email';

  const isAlertEntryActive = activeTab === 'email';

  useEffect(() => {
    if (isTestAccount && locations.length && !wizardData?.selectedVault) {
      const testProductLocation = locations.find((loc) => loc.IsTestProduct);
      if (testProductLocation) {
        setWizardData({ ...wizardData, selectedVault: testProductLocation });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestAccount, locations]);

  const minPrice = locations?.length
    ? Math.min(...locations.map((l) => l.PricePerUnit))
    : 0;
  const firstLowestVault = locations.find(
    (l) => l.PricePerUnit === minPrice && l.LocationType !== 12,
  );

  const deliveryLocation =
    !isProductPage &&
    storageProduct?.Locations.find((location) => location.LocationType === 12);

  useEffect(() => {
    let title = t('gtt.common.addAnotherAlert');
    if (isAddAlertFlow) title = t('gtt.title.priceAlertEntry');
    else if (wizardData?.isEditAlert) title = t('gtt.title.editAlert');
    setWizardTitle(title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAddAlertFlow, wizardData?.isEditAlert]);

  useEffect(() => {
    if (!isAddAlertFlow) {
      setGoBackHandler(() => handleBack);
    }
    return () => setGoBackHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddAlertFlow]);

  useEffect(() => {
    // On the Edit Alert screen the modal X should dismiss back to the Active
    // tab (no changes saved) rather than closing the whole alert modal.
    if (wizardData?.isEditAlert) {
      setCloseHandler(() => handleBack);
      return () => setCloseHandler(null);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardData?.isEditAlert]);

  const isValidVaultSelected = () =>
    !!wizardData?.selectedVault && wizardData.selectedVault.LocationType !== 12;

  const vaultPriceLocation = isValidVaultSelected()
    ? wizardData.selectedVault
    : firstLowestVault;
  const priceLocation = isAlertEntryActive
    ? vaultPriceLocation
    : deliveryLocation;

  const unitPrice = useProductPrice(
    wizardData?.storageProduct,
    priceLocation,
    isProductPage,
    isAlertEntryActive,
  );

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [dispatch, accountUid]);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchPortfolioBalances({ accountUid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userInfo?.UserUid) {
      dispatch(fetchUserPhoneNumbers({ userUid: userInfo.UserUid }));
    }
  }, [dispatch, userInfo?.UserUid]);

  // Edit Alert reads the alert's saved Phone/country by deriving from the
  // fresh price alerts LIST slice (see currentAlert above) — no dedicated
  // singular fetch. The list is already refreshed on modal open and after
  // every save, and (unlike the singular slice) is updated by those refetches
  // from the confirmed-fresh collection endpoint.

  // Download / view the SMS terms-of-use agreement PDF. Mirrors the working
  // TermsPage flow: the saga GETs /accounts/{accountUid}/agreements/pdf as a
  // blob and hands it back via agreementFetchSuccess, which we open in a new
  // tab. AGREEMENT_TYPES[2] matches the type rendered by AgreementDisplay in
  // the terms modal below.
  const handleDownloadAgreement = () => {
    dispatch(
      fetchAccountAgreement({
        accountUid,
        agreementType: AGREEMENT_TYPES[8],
        setIsLoading: setIsAgreementDownloading,
        agreementFetchSuccess: (data) => {
          if (data) {
            const objectUrl = window.URL.createObjectURL(data);
            window.open(objectUrl, '_blank', 'noopener,noreferrer');
          }
        },
      }),
    );
  };

  // Accept the SMS terms of use. Ticking the Text checkbox is the consent, so
  // this only closes the modal; the acceptance is NOT posted here. The
  // POST /accounts/{accountUid}/agreements call is deferred to the Add Alert
  // submit (see handleSubmitClick) so acceptance is recorded server-side only
  // when the user actually commits the alert.
  const handleAcceptAgreement = () => {
    setIsTermsModalOpen(false);
  };

  const handlePhoneSaved = (newPhone) => {
    // The user just picked a number in the drawer — record it as the
    // in-session override so it wins over the alert's list-derived phone.
    setPendingPhone(newPhone);
  };

  const livePrice = unitPrice;

  // price_alert_started — fired when the Alert Entry screen opens, tagged with
  // where the journey began. Edit isn't a start; reactivation emits its own
  // started event on submit, so both are skipped here to avoid a duplicate.
  // source: the email/SMS deep-link channel when present, otherwise 'my_alerts'
  // — every other add (bell, "Add Another", or a fresh add) starts in-app.
  useEffect(() => {
    if (wizardData?.isEditAlert || wizardData?.isReactivation) return;
    const deepLink = retrieveFromSessionStorage(PRICE_ALERT_DEEPLINK);
    const source = deepLink?.source || ENTRY_POINTS.MY_ALERTS;
    trackPriceAlertStarted({
      ecommerceGtmEvent,
      productName: storageProduct?.ShortCaption,
      weight: storageProduct?.WeightCaption,
      currentPrice: livePrice,
      source,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPriceBelowLive = (value) => {
    const amount = parseFloat(value);
    return (
      value !== '' &&
      value !== undefined &&
      !Number.isNaN(amount) &&
      Number(livePrice) > 0 &&
      amount < Number(livePrice)
    );
  };

  const validateForm = (values) => {
    const errors = {};
    if (values.CashAmount !== '' && !isPriceBelowLive(values.CashAmount)) {
      errors.CashAmount = t('gtt.errors.priceMustBeBelowLive', {
        livePrice: formatMoneyNumeral(livePrice),
      });
    }
    return errors;
  };

  const handleSubmitClick = (formik) => {
    const cashAmount = formik?.values?.CashAmount;
    if (!cashAmount) return;

    // structuredPhone already encodes the correct priority for what to save:
    // the user's in-session change > the alert's own saved phone (untouched
    // edit) > the account default. Do NOT use `pendingPhone || initialPhone`
    // here — pendingPhone is null on an untouched edit, which would wrongly
    // PATCH the account phone over the alert's existing saved number.
    const phone = isSmsChecked ? structuredPhone : null;
    wasAlertSuccessRef.current = false;

    // Analytics: capture the pre-save diff now, because the list refetch on
    // success mutates `currentAlert` to the new values. Channel = whether SMS
    // is on (email is always on); represented with the same wording the list
    // uses ("Email + Text" / "Email").
    const newPrice = parseFloat(cashAmount);
    const editedFields = {};
    if (wizardData?.isEditAlert) {
      const oldPrice = currentAlert?.TargetPrice;
      if (oldPrice !== newPrice) {
        editedFields.old_price = oldPrice;
        editedFields.new_price = newPrice;
      }
      const oldSms = !!currentAlert?.NotifyViaSMS;
      if (oldSms !== isSmsChecked) {
        editedFields.old_channel = oldSms ? 'Email + Text' : 'Email';
        editedFields.new_channel = isSmsChecked ? 'Email + Text' : 'Email';
      }
    }

    const sharedPayload = {
      accountUid,
      ProductCode: storageProduct?.ProductCode,
      TargetPrice: parseFloat(cashAmount),
      PriceAtSubmission: livePrice,
      Phone: phone,
      Email: userInfo?.Email,
      setIsLoading: setIsSubmitting,
      // Surface BE validation errors (73449) in the shared ErrorDrawer.
      // Saga calls setError(message) on failure and setError(false) on success.
      setError: (message) => (message ? showError({ message }) : hideError()),
      onSuccess: (savedAlert) => {
        wasAlertSuccessRef.current = true;
        setWizardData({ alertPrice: cashAmount, isSmsChecked });

        if (wizardData?.isEditAlert) {
          // Row 6 — a single event carrying every changed field (old_price/
          // new_price, old_channel/new_channel); unchanged fields are omitted.
          // source: the email/SMS deep-link channel when the edit came from a
          // notification link, otherwise 'my_alerts' (opened from My Alerts).
          const editDeepLink = retrieveFromSessionStorage(PRICE_ALERT_DEEPLINK);
          trackPriceAlertEdited({
            ecommerceGtmEvent,
            alertId: wizardData?.priceAlertUid,
            changes: editedFields,
            source: editDeepLink?.source || ENTRY_POINTS.MY_ALERTS,
          });
        } else if (wizardData?.isReactivation) {
          // Row 9 — renewing an expired alert via "Set Again". A reactivation
          // is also a fresh alert-setting journey, so emit price_alert_started
          // alongside price_alert_reactivated. It also POSTs a brand-new alert
          // record, so it counts as an alert added.
          // source: the email/SMS deep-link channel when the renewal came from a
          // notification link, otherwise 'my_alerts' (launched from My Alerts).
          const reactivationDeepLink = retrieveFromSessionStorage(
            PRICE_ALERT_DEEPLINK,
          );
          const reactivationSource =
            reactivationDeepLink?.source || ENTRY_POINTS.MY_ALERTS;
          trackPriceAlertStarted({
            ecommerceGtmEvent,
            productName: storageProduct?.ShortCaption,
            weight: storageProduct?.WeightCaption,
            currentPrice: livePrice,
            source: reactivationSource,
          });
          trackPriceAlertReactivated({
            ecommerceGtmEvent,
            alertId: wizardData?.originalAlertUid,
            originalAlertPrice: wizardData?.originalAlertPrice,
            newAlertPrice: newPrice,
            source: reactivationSource,
          });
          trackPriceAlertCreated({
            ecommerceGtmEvent,
            alertId: savedAlert?.PriceAlertUid,
            productName: storageProduct?.ShortCaption,
            alertPrice: newPrice,
            isSmsChecked,
            currentPrice: livePrice,
          });
        } else {
          // Row 4 — a fresh alert created.
          trackBuyStarted({
            ecommerceGtmEvent,
            alertId: savedAlert?.PriceAlertUid,
            productName: storageProduct?.ShortCaption,
            entryPoint: ENTRY_POINTS.PRICE_ALERT_SET,
          });
          trackPriceAlertCreated({
            ecommerceGtmEvent,
            alertId: savedAlert?.PriceAlertUid,
            productName: storageProduct?.ShortCaption,
            alertPrice: newPrice,
            isSmsChecked,
            currentPrice: livePrice,
          });
        }
      },
      handleNext: () => {
        if (!wasAlertSuccessRef.current) return;
        if (wizardData?.isEditAlert) {
          // Editing: save and return to the Active tab (no confirmation
          // screen). Refresh the list so the edited alert shows updated.
          // Pass only the delta — setWizardData already merges against the
          // latest state, so spreading the stale render-time `wizardData`
          // closure here would clobber the isSmsChecked/alertPrice fields
          // onSuccess just wrote above.
          setWizardData({ initialTab: 'Active' });
          dispatch(
            fetchPriceAlerts({
              accountUid,
              ...(storageProduct?.ProductCode && {
                productCode: storageProduct.ProductCode,
              }),
            }),
          );
          handleBack();
        } else {
          handleNext();
        }
      },
    };

    const submitAlert = () =>
      dispatch(
        wizardData?.isEditAlert
          ? patchPriceAlert({
              ...sharedPayload,
              priceAlertUid: wizardData?.priceAlertUid,
            })
          : postPriceAlert(sharedPayload),
      );

    // Record SMS terms-of-use acceptance server-side at submit time (moved here
    // from the terms modal's Accept button). Only when SMS notifications are on
    // — the agreement is the SMS terms and consent is required only for SMS
    // (the Text checkbox being ticked is the consent).
    // Gate the alert POST/PATCH on the agreement succeeding.
    if (isSmsChecked) {
      dispatch(
        submitAccountAgreements({
          accountUid,
          agreementType: AGREEMENT_TYPES[8],
          setIsLoading: setIsSubmittingAgreement,
          onSuccess: submitAlert,
        }),
      );
    } else {
      submitAlert();
    }
  };
  // Ticking the Text checkbox is the SMS consent, so there is no separate
  // consent gate here; SMS off imposes no consent requirement.
  const isReviewBuyDisabled = (values) => !isPriceBelowLive(values?.CashAmount);

  return (
    <>
      <SectionLoader isLoading={isLoading}>
        <BasicModalBody style={{ position: 'relative' }}>
          <ProductHeadingRow>
            <ProductName
              marginBottom={4}
              isGS={brokerCode === 'GS'}
            >{`${storageProduct?.ShortCaption}`}</ProductName>
            <ProductHeadingPrice>
              <ProductPrice>{formatMoneyNumeral(unitPrice)}</ProductPrice>
            </ProductHeadingPrice>
          </ProductHeadingRow>

          <Formik
            innerRef={formikRef}
            initialValues={{
              CashAmount: wizardData?.orderValues?.CashAmount || '',
            }}
            enableReinitialize
            validate={validateForm}
            validateOnBlur
            validateOnChange={false}
          >
            {(formik) => {
              const {
                values,
                setFieldValue,
                setFieldTouched,
                errors,
                touched,
              } = formik;
              const showPriceError =
                !isPriceFocused && touched.CashAmount && errors.CashAmount;

              return (
                <Form>
                  <NotifyLabel>
                    {t('gtt.labels.notifyWhenPriceReaches')}
                  </NotifyLabel>
                  <FormContent>
                    <PriceInputWrapper>
                      <DollarSign>$</DollarSign>
                      <PriceInput
                        type="text"
                        inputMode="decimal"
                        name="CashAmount"
                        data-cy="input-alert-target-price"
                        isError={!!showPriceError}
                        value={values.CashAmount}
                        onChange={(e) => {
                          // allow only digits and a single decimal point
                          let sanitized = e.target.value.replace(
                            /[^0-9.]/g,
                            '',
                          );
                          const firstDot = sanitized.indexOf('.');
                          if (firstDot !== -1) {
                            sanitized =
                              sanitized.slice(0, firstDot + 1) +
                              sanitized.slice(firstDot + 1).replace(/\./g, '');
                          }
                          // limit to a maximum of 2 decimal places
                          const [intPart, decPart] = sanitized.split('.');
                          if (decPart !== undefined) {
                            sanitized = `${intPart}.${decPart.slice(0, 2)}`;
                          }
                          setFieldValue('CashAmount', sanitized);
                          // hide the validation message while the user edits
                          setIsPriceFocused(true);
                        }}
                        onFocus={() => setIsPriceFocused(true)}
                        onBlur={() => {
                          setIsPriceFocused(false);
                          setFieldTouched('CashAmount', true);
                        }}
                        placeholder=""
                      />
                    </PriceInputWrapper>
                    {showPriceError && (
                      <InputError>{errors.CashAmount}</InputError>
                    )}
                  </FormContent>
                  <InfoBanner>
                    <InfoIcon
                      fill={themeColors.colorTextSecondary}
                      style={{ width: 15, height: 15 }}
                    />{' '}
                    {t('gtt.labels.validFor')}
                  </InfoBanner>
                  <Divider />
                  <NotifyViaLabel>{t('gtt.labels.notifyVia')}</NotifyViaLabel>
                  {brokerCode === 'GS' && (
                    <RowItemElement
                      label={
                        <AlertRowLabel>{t('gtt.labels.email')}</AlertRowLabel>
                      }
                      value={
                        <AlertRowValue>
                          <StyledEnvelope />
                          <AlertRowText>{displayEmail}</AlertRowText>
                        </AlertRowValue>
                      }
                      isNotButton
                    />
                  )}
                  <RowItemElement
                    labelFontSize="14px"
                    label={
                      <SmsLabel>
                        <CheckboxGTT
                          checked={isSmsChecked}
                          onClick={() => setSmsOverride(!isSmsChecked)}
                          ariaLabel={t('gtt.labels.text')}
                        />
                        {t('gtt.labels.text')}
                      </SmsLabel>
                    }
                    value={
                      <AlertRowValue>
                        <StyledMobileDevice />
                        {formattedPhoneNumber}
                      </AlertRowValue>
                    }
                    onClick={() => {
                      // SectionLoader's overlay should already prevent this,
                      // but guard explicitly too — opening this drawer while
                      // the alert's phone/country hasn't been confirmed
                      // fresh yet is exactly what produces a stale flag that
                      // never self-corrects once the drawer is open.
                      if (isLoading) return;
                      setIsChangeMobileNumberOpen(true);
                    }}
                    clickLabel={
                      (structuredPhone ? isSmsChecked : true) && (
                        <RowItemClickLabel>
                          {structuredPhone
                            ? t('buyWizard.changeLabel')
                            : t('common.add')}
                        </RowItemClickLabel>
                      )
                    }
                  />
                  {isSmsChecked && (
                    <ConsentRow>
                      <SmsConsentText>
                        {t('gtt.labels.smsConsent', {
                          brokerName:
                            brokerCode === 'GS' ? GS : settings?.BrokerName,
                        })}{' '}
                        <TermsLink
                          role="link"
                          tabIndex={0}
                          data-cy="link-alert-terms-and-conditions"
                          onClick={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            setIsTermsModalOpen(true);
                          }}
                        >
                          {t('gtt.labels.termsAndConditions')}
                        </TermsLink>
                      </SmsConsentText>
                    </ConsentRow>
                  )}
                  <FullWidthButtonWrapper>
                    <ModalButtons
                      isHorizontal={!isAddAlertFlow}
                      showOnlyPrimary={isAddAlertFlow}
                      primaryButtonProps={{
                        type: 'button',
                        label: wizardData?.isEditAlert
                          ? t('gtt.common.editAlert')
                          : t('gtt.common.submit'),
                        marginTop: 10,
                        textTransform: 'capitalize',
                        fontSize: '16px',
                        disabled: isReviewBuyDisabled(values),
                        'data-cy': 'create-alert',
                        onClick: () => handleSubmitClick(formik),
                      }}
                      secondaryButtonProps={{
                        label: t('common.cancel'),
                        onClick: handleBack,
                        marginTop: 10,
                        textTransform: 'capitalize',
                        fontSize: '16px',
                        'data-cy': 'cancel-edit-alert',
                      }}
                    />
                  </FullWidthButtonWrapper>

                  <ErrorDrawer
                    open={isErrorOpen}
                    errorTitle={errorTitle}
                    errorMessage={errorMessage}
                    onClose={hideError}
                  />
                </Form>
              );
            }}
          </Formik>
        </BasicModalBody>
      </SectionLoader>
      <ChangeMobileNumber
        // IntlTelInput (inside ChangeMobileNumber) is uncontrolled — it only
        // reads currentIso3CountryCode/currentPhone once, at mount. Keying on
        // the resolved phone forces a fresh remount whenever structuredPhone
        // changes, so if the list refreshes to a newer country while the
        // drawer is open (or opened early), the flag self-heals instead of
        // requiring the user to manually close and reopen the drawer.
        key={`${structuredPhone?.Iso3CountryCode}-${structuredPhone?.AreaCode}-${structuredPhone?.LocalNumber}`}
        open={isChangeMobileNumberOpen}
        setOpen={setIsChangeMobileNumberOpen}
        onPhoneSaved={handlePhoneSaved}
        currentIso3CountryCode={structuredPhone?.Iso3CountryCode}
        currentPhone={formatPhoneNumberNationalAsString(
          structuredPhone,
          countries,
          selectedISOCodesListToIgnoreAreaCode,
        )}
      />
      <AlertInfoDrawer
        isOpen={isAlertInfoOpen}
        onClose={() => setIsAlertInfoOpen(false)}
      />
      <PrintModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        onCloseButton={handleAcceptAgreement}
        buttonText={t('common.accept')}
        variant="lg"
        hidePrint
        shouldScrollDown
        hideHeaderShadow
        closeIconRight
        isLoading={isAgreementContentLoading || isSubmittingAgreement}
        isDownloading={isAgreementDownloading}
        onDownload={handleDownloadAgreement}
        downloadLabel={t('gtt.labels.downloadAgreement')}
      >
        <AgreementDisplay
          type={AGREEMENT_TYPES[8]}
          signTheDoc={false}
          hideLoader
        />
      </PrintModal>
    </>
  );
};

AlertEntry.propTypes = {
  wizardData: PropTypes.shape({
    selectedPaymentMethod: PropTypes.shape({
      PaymentMethodType: PropTypes.number,
      PaymentMethodUid: PropTypes.string,
    }),
    storageProduct: PropTypes.shape({
      ProductCaption: PropTypes.string,
      ProductPrice: PropTypes.number,
      Locations: PropTypes.arrayOf(
        PropTypes.shape({
          BuyMinimumAmount: PropTypes.number,
        }),
      ),
      ProductCode: PropTypes.string,
      ShortCaption: PropTypes.string,
      WeightCaption: PropTypes.string,
      SymbolCode: PropTypes.string,
    }),
    activeTab: PropTypes.string,
    selectedPlan: PropTypes.string,
    activeType: PropTypes.string,
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
      PricePerUnit: PropTypes.number,
      BuyMinimumAmount: PropTypes.number,
      BuyForDeliveryMinimumAmount: PropTypes.number,
    }),
    orderValues: PropTypes.shape({
      CashAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      QuantityAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    selectedAddress: PropTypes.shape({}),
    paymentMethodType: PropTypes.string,
    isSegregated: PropTypes.bool,
    isEditAlert: PropTypes.bool,
    isSmsChecked: PropTypes.bool,
    isReactivation: PropTypes.bool,
    originalAlertUid: PropTypes.string,
    originalAlertPrice: PropTypes.number,
    phone: PropTypes.shape({
      Iso3CountryCode: PropTypes.string,
      AreaCode: PropTypes.string,
      LocalNumber: PropTypes.string,
    }),
    priceAlertUid: PropTypes.string,
    productPrices: PropTypes.shape({
      Locations: PropTypes.arrayOf(
        PropTypes.shape({
          BuyMinimumAmount: PropTypes.number,
        }),
      ),
    }),
    calculation: PropTypes.shape({
      newPricePerUnit: PropTypes.number,
      paymentMethodStepData: PropTypes.shape({
        DataWithCCFee: PropTypes.shape({
          ClientPricePerUnit: PropTypes.number,
        }),
      }),
    }),
  }),
  isProductPage: PropTypes.bool,
  isAddAlertFlow: PropTypes.bool,
  setWizardData: PropTypes.func,
  handleNext: PropTypes.func,
  handleBack: PropTypes.func,
  setWizardTitle: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  setCloseHandler: PropTypes.func,
  // isUtmaAccount: PropTypes.bool,
  // isSegregated: PropTypes.bool,
  // isInstaVaultProduct: PropTypes.bool,
};

export default AlertEntry;
